import { useState, useMemo, useCallback, useEffect } from "react";
import { useLocalStorage } from "./useLocalStorage";
import { recordStatuses, isLocalOrStaging } from "../constants";
import Airtable from "airtable";

// Vite projects use a different mechanism for accessing environment variables; try that if it's available
const cmsBase =
  import.meta.env && import.meta.env.VITE_AIRTABLE_API_KEY
    ? new Airtable({ apiKey: import.meta.env.VITE_AIRTABLE_API_KEY }).base(import.meta.env.VITE_AIRTABLE_BASE_ID)
    : new Airtable({ apiKey: process.env.AIRTABLE_API_KEY }).base(process.env.AIRTABLE_BASE_ID);

export const useAirtableCMS = ({ tableName, viewName = "Grid view", fieldMapping, asList = true, sortBy, filterBy }) => {
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);
  const [cmsObjects, setCMSObjects, lastUpdated] = useLocalStorage(tableName, {});
  const cmsTable = useMemo(() => cmsBase(tableName), [tableName]);

  const fetchIDs = useCallback(async () => {
    const localIDs = new Set(Object.keys(cmsObjects));
    const remoteIDs = new Set();
    // While somewhat inefficient, we have to diff the entire set of IDs to account for the possibility of deleted records
    cmsTable
      .select({
        view: viewName,
        fields: [],
      })
      .eachPage(
        (records, fetchNextPage) => {
          records.forEach((record) => {
            // Primary keys can change; record ID does not, use that
            remoteIDs.add(record.id);
          });

          fetchNextPage();
        },
        (err) => {
          if (err) {
            console.error(err);
            setError(err);
            setDone(true);
          } else {
            const deletedRecords = localIDs.difference(remoteIDs);
            if (deletedRecords.size > 0) {
              const mergedObject = {
                ...cmsObjects,
              };
              for (let key of deletedRecords.values()) {
                delete mergedObject[key];
              }
              setCMSObjects(mergedObject);
            }
            setDone(true);
          }
        },
      );
  }, [cmsObjects, setCMSObjects, viewName, cmsTable]);

  const fetchUpdates = useCallback(async () => {
    const lastRequested = localStorage.getItem(`lastAirtableFetch-${tableName}`);
    if (lastRequested != null && Date.now() - Number(JSON.parse(lastRequested)) < 3000) {
      // Only need to request this once: don't send more than one request per second
      return;
    } else {
      localStorage.setItem(`lastAirtableFetch-${tableName}`, JSON.stringify(Date.now()));
    }
    const updatedRecords = {};
    const unpublishedRecords = [];
    cmsTable
      .select({
        view: viewName,
        filterByFormula: `IS_AFTER({Last Modified}, '${new Date(lastUpdated).toISOString()}')`,
        fields: [...Object.values(fieldMapping), "Status"],
      })
      .eachPage(
        (records, fetchNextPage) => {
          records.forEach((record) => {
            const recordStatus = record.get("Status");
            if (recordStatus === recordStatuses.PUBLISHED || (isLocalOrStaging && recordStatus === recordStatuses.PREVIEW)) {
              const updatedRecord = {};
              Object.entries(fieldMapping).forEach(([key, value]) => {
                updatedRecord[key] = record.get(value);
              });
              updatedRecords[record.id] = updatedRecord;
            } else {
              unpublishedRecords.push(record.id);
            }
          });

          fetchNextPage();
        },
        (err) => {
          if (err) {
            console.error(err);
            setError(err);
            setDone(true);
          } else {
            if (Object.keys(updatedRecords).length > 0 || unpublishedRecords.length > 0) {
              const mergedObject = {
                ...cmsObjects,
                ...updatedRecords,
              };
              for (let key of unpublishedRecords) {
                delete mergedObject[key];
              }
              setCMSObjects(mergedObject);
            }
            fetchIDs();
          }
        },
      );
  }, [cmsObjects, tableName, cmsTable, fieldMapping, lastUpdated, setCMSObjects, viewName, fetchIDs]);

  useEffect(() => {
    fetchUpdates().catch((err) => {
      console.error(err);
      setDone(true);
      setError({
        type: "UNEXPECTED_ERROR",
        message: `Unexpected error when trying to fetch CMS updates: ${err}`,
      });
    });
  }, [fetchUpdates]);

  const response = {
    metadata: {
      error,
      done,
    },
  };

  try {
    if (asList) {
      let objectList = Object.values(cmsObjects);
      if (filterBy != null) {
        objectList = objectList.filter(filterBy);
      }

      if (sortBy != null) {
        objectList = objectList.sort(sortBy);
      }

      response.data = objectList;
    } else {
      response.data = cmsObjects;
    }
  } catch (err) {
    if (asList) {
      response.data = [];
    } else {
      response.data = {};
    }
    console.error(err);
    setDone(true);
    setError({
      type: "UNEXPECTED_ERROR",
      message: `Unexpected error when trying to sort/filter CMS data: ${err}`,
    });
  }

  return response;
};

export default useAirtableCMS;
